import { useMemo, useState } from 'react';
import { Phone, Mail, Send, Check, MapPin, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { API_ENDPOINTS } from '@/config/api';
import { useCart } from '@/context/CartContext';

/** Relativne /gallery/... putanje u pun https URL za mejl (i backend i dalje može da doda domen). */
function absolutePublicUrl(path, origin) {
  if (!path || typeof path !== 'string') return path;
  const t = path.trim();
  if (t.startsWith('http://') || t.startsWith('https://')) return t;
  if (t.startsWith('//')) return `https:${t}`;
  if (!origin) return t;
  if (t.startsWith('/')) return `${origin}${t}`;
  return `${origin}/${t}`;
}

export default function CallToAction() {
  const { items, hasChairItems, clearCart } = useCart();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const messagePlaceholder = useMemo(() => {
    if (items.length > 0) {
      const base =
        'Navedite željeni materijal (tkanina, drvo, dezen, boja…) i dimenzije nameštaja u centimetrima. Dodajte rok, posebne zahteve ili druga pitanja.';
      if (hasChairItems) {
        return `${base} Za stolice navedite i visinu sedišta, širinu i dubinu gde je potrebno.`;
      }
      return base;
    }
    return 'Pišite nam kako možemo da vam pomognemo...';
  }, [hasChairItems, items.length]);

  const messageLabel = useMemo(() => {
    if (items.length > 0) return 'Napomena — materijal, dimenzije nameštaja i ostalo *';
    return 'Poruka *';
  }, [items.length]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const cartItemsPayload = items.map((i) => ({
      productId: i.productId,
      name: i.name,
      category: i.category,
      imageUrl: absolutePublicUrl(i.image, origin),
      quantity: i.quantity,
    }));

    try {
      await axios.post(API_ENDPOINTS.CONTACT, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        message: formData.message,
        cartItems: cartItemsPayload.length > 0 ? cartItemsPayload : null,
      });

      toast.success('Upit uspešno poslat! Javićemo vam se uskoro.');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        message: '',
      });
      clearCart();
    } catch (error) {
      console.error('Error submitting contact form:', error);

      if (error.response) {
        const errorMessage =
          error.response.data?.message ||
          error.response.data?.error ||
          'Došlo je do greške pri slanju poruke. Molimo pokušajte ponovo.';
        toast.error(errorMessage);
      } else if (error.request) {
        toast.error('Nema konekcije sa serverom.');
      } else {
        toast.error('Došlo je do neočekivane greške. Molimo pokušajte ponovo.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="pt-8 lg:pt-12 pb-20 lg:pb-28 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-20">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-foreground mb-4 lg:mb-6">
            Kontaktirajte nas
          </h2>
          <p className="text-base lg:text-lg text-muted-foreground leading-relaxed">
            Spremni smo da odgovorimo na sva vaša pitanja i pomognemo vam u odabiru savršenog nameštaja
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-10 lg:gap-12">
          <div className="lg:col-span-2 space-y-6">
            <Card className="border-2 border-border/50 bg-card hover:border-primary/20 transition-colors shadow-md">
              <CardContent className="p-6 lg:p-8">
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/5 rounded-xl flex items-center justify-center flex-shrink-0 border-2 border-primary/10">
                      <Phone className="w-6 h-6 text-primary" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground mb-1 text-base">Telefon</h3>
                      <p className="text-muted-foreground text-sm lg:text-base">+381 64 1722224</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 pt-4 border-t border-border/30">
                    <div className="w-12 h-12 bg-primary/5 rounded-xl flex items-center justify-center flex-shrink-0 border-2 border-primary/10">
                      <Mail className="w-6 h-6 text-primary" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground mb-1 text-base">Email</h3>
                      <p className="text-muted-foreground text-sm lg:text-base">elvisahmetovic2015@gmail.com</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 pt-4 border-t border-border/30">
                    <div className="w-12 h-12 bg-primary/5 rounded-xl flex items-center justify-center flex-shrink-0 border-2 border-primary/10">
                      <MapPin className="w-6 h-6 text-primary" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground mb-1 text-base">Adresa</h3>
                      <p className="text-muted-foreground text-sm lg:text-base">Prijepolje, Srbija</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 pt-4 border-t border-border/30">
                    <div className="w-12 h-12 bg-primary/5 rounded-xl flex items-center justify-center flex-shrink-0 border-2 border-primary/10">
                      <Clock className="w-6 h-6 text-primary" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground mb-1 text-base">Radno vreme</h3>
                      <p className="text-muted-foreground text-sm lg:text-base">Pon-Sub: 08:00 - 16:00</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-3 pt-4">
              {[
                'Besplatna konsultacija',
                'Dostava na kućnu adresu',
                'Stručna montaža',
                'Produžena garancija',
              ].map((benefit, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 border border-primary/20">
                    <Check className="w-4 h-4 text-primary" strokeWidth={2.5} />
                  </div>
                  <span className="text-sm lg:text-base text-foreground/80">{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-3 space-y-6">
            {items.length > 0 && (
              <Card className="border-2 border-primary/15 bg-gallery-card shadow-md">
                <CardContent className="p-6 lg:p-8">
                  <h3 className="text-lg font-bold text-foreground mb-1">Vaš upit uključuje</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Pregled stavki iz korpe biće poslat uz upit. U napomeni ispod navedite{' '}
                    <strong className="font-medium text-foreground/90">izbor materijala</strong> i{' '}
                    <strong className="font-medium text-foreground/90">dimenzije nameštaja</strong>
                    {hasChairItems ? ' (za stolice i ostale komade, u cm gde je potrebno)' : ''}, kao i ostale zahteve.
                  </p>
                  <ul className="space-y-3 max-h-56 overflow-y-auto pr-1">
                    {items.map((item) => (
                      <li
                        key={item.productId}
                        className="flex gap-3 text-sm border-b border-black/[0.06] pb-3 last:border-0 last:pb-0"
                      >
                        <div className="w-14 h-14 rounded-md overflow-hidden bg-white border border-black/[0.06] shrink-0">
                          <img src={item.image} alt="" className="w-full h-full object-contain" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-foreground leading-snug">{item.name}</p>
                          <p className="text-muted-foreground text-xs mt-0.5">Količina: {item.quantity}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            <Card className="border-2 border-border/50 bg-card shadow-md">
              <CardContent className="p-8 lg:p-10">
                <h3 className="text-2xl lg:text-3xl font-bold text-foreground mb-8">
                  {items.length > 0 ? 'Kreirajte upit' : 'Pošaljite nam poruku'}
                </h3>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-semibold text-foreground mb-2">
                        Ime *
                      </label>
                      <Input
                        id="firstName"
                        name="firstName"
                        type="text"
                        required
                        value={formData.firstName}
                        onChange={handleChange}
                        placeholder="Unesite vaše ime"
                        className="w-full h-12"
                      />
                    </div>

                    <div>
                      <label htmlFor="lastName" className="block text-sm font-semibold text-foreground mb-2">
                        Prezime *
                      </label>
                      <Input
                        id="lastName"
                        name="lastName"
                        type="text"
                        required
                        value={formData.lastName}
                        onChange={handleChange}
                        placeholder="Unesite vaše prezime"
                        className="w-full h-12"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-2">
                        Email adresa *
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="vas@email.com"
                        className="w-full h-12"
                      />
                    </div>

                    <div>
                      <label htmlFor="phoneNumber" className="block text-sm font-semibold text-foreground mb-2">
                        Broj telefona *
                      </label>
                      <Input
                        id="phoneNumber"
                        name="phoneNumber"
                        type="tel"
                        required
                        value={formData.phoneNumber}
                        onChange={handleChange}
                        placeholder="+381 ..."
                        className="w-full h-12"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-foreground mb-2">
                      {messageLabel}
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={messagePlaceholder}
                      rows={6}
                      className="w-full resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    disabled={isSubmitting}
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium transition-all duration-300 shadow-md hover:shadow-lg h-14 text-base"
                  >
                    {isSubmitting ? (
                      'Šalje se...'
                    ) : (
                      <>
                        {items.length > 0 ? 'Pošalji upit' : 'Pošalji poruku'}
                        <Send className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
}
